'use client';

import Loader from '@pickleballinc/features/src/Loader/Loader';
import { useEffect } from 'react';

import { TeamLeagueDistrict } from '@/entities';
import { PermissionRole, UNLIMITED_ACCESS } from '@/entities/PermissionRoles';
import { SeasonListItem } from '@/entities/SeasonList';
import { TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { Timezone } from '@/entities/TimeZones';
import { useUserPermissionQuery } from '@/hooks/permission/useUserPermissionQuery';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { useSeasonDistrictDivisionStore } from '@/store/season-district-division-store';
import { useTeamLeagueStore } from '@/store/team-league-store';
import { useTiebreakerStore } from '@/store/tie-breaker-store';

interface TeamLeagueDetailsLayoutProps {
	organizationUuid: string;
	teamLeaguesUuid: string;
	teamLeaguesSlug: string;
	children: React.ReactNode;
	timeZones: Timezone[];
	timeZoneId: number;
	teamLeagueResponse: TeamLeagueInfo;
	seasonsInfo: SeasonListItem[];
	districtsInfo: TeamLeagueDistrict[];
}

export const TeamLeagueDetailsLayoutContent = ({
	organizationUuid,
	teamLeaguesUuid,
	teamLeaguesSlug,
	timeZones,
	timeZoneId,
	children,
	teamLeagueResponse,
	seasonsInfo,
	districtsInfo
}: TeamLeagueDetailsLayoutProps) => {
	const { data: permissions, isLoading: isLoadingPermissions } = useUserPermissionQuery();
	const store = useOrganizationStore();
	const teamLeagueStore = useTeamLeagueStore();
	const permissionStore = usePermissionStore();
	const tieBreakerStore = useTiebreakerStore();

	const seasonDistrictDivisionStore = useSeasonDistrictDivisionStore();

	useEffect(() => {
		store.setOrganizationUuid(organizationUuid);
		store.setTeamLeagueUuid(teamLeaguesUuid);
		store.setTeamLeagueSlugUuid(teamLeaguesSlug);
		const timezone = timeZones.find((tz) => +tz.id === timeZoneId);

		if (timezone) {
			store.setTeamLeagueTimeZone(timezone);
		}

		tieBreakerStore.setTieBreakerLineupVisibility(teamLeagueResponse.tieBreakerLineupOption);
		store.setLineupVisibility(teamLeagueResponse.publicLineupVisibility);
		store.setOpponentLineupVisibility(teamLeagueResponse.opponentLineupVisibility);
		teamLeagueStore.setTeamLeagueInfo(teamLeagueResponse);

		seasonDistrictDivisionStore.setSeasonsInfo(seasonsInfo);
		seasonDistrictDivisionStore.setDistrictsInfo(districtsInfo);
	}, [organizationUuid, teamLeaguesUuid, teamLeaguesSlug, seasonsInfo, districtsInfo]);

	useEffect(() => {
		if (!isLoadingPermissions && permissions?.data) {
			permissionStore.setPermission(permissions.data.permission);
			const unlimitedAccess = UNLIMITED_ACCESS.some((v) => v === permissions.data.permission);
			permissionStore.setUnlimitedAccess(unlimitedAccess);
			permissionStore.setGamedayAccess(
				unlimitedAccess ||
					permissions.data.permission === PermissionRole.CAN_MANAGE_GAMEDAYS ||
					permissions.data.permission === PermissionRole.TEAM_LEAGUE_MANAGER
			);
			permissionStore.setManageTeamLeague(unlimitedAccess || permissions.data.permission === PermissionRole.TEAM_LEAGUE_MANAGER);
			if (permissions?.data?.teamIds?.length) {
				permissionStore.setTeamIds(permissions.data.teamIds);
			}
			permissionStore.setCaptainTeamIds(permissions.data.captainTeamIds ?? []);
		}
	}, [permissions]);

	if (isLoadingPermissions || !permissions || !teamLeagueStore.teamLeagueInfo || !seasonDistrictDivisionStore) {
		return <Loader backgroundColor="bg-[#EF6820] opacity-100" />;
	}

	return children;
};
